import React from "react";
import { useMemo } from "react";
import { IconSymbol } from "@abyss/web/ui/IconSymbol";
import { Link } from "@abyss/web/ui/Link";
import { GridDataTableColumn } from "../GridDataTable/GridDataTableColumn";

type IconName = React.ComponentProps<typeof IconSymbol>["icon"];

/**
 * Options accepted by `useDataTableLinkColumnMapper`.
 */
export interface UseDataTableLinkColumnMapperOptions {
  /** The column definitions that may contain link-column properties. */
  columns: GridDataTableColumn[];
}

/**
 * Resolves all `{placeholderName}` placeholders in a URL template.
 *
 * 1. `{value}` is always resolved first using the provided `value` parameter.
 * 2. If `linkUrlHasPlaceHolder` is falsy, returns immediately after `{value}`
 *    resolution — no row-based placeholders are processed.
 * 3. Otherwise, remaining `{fieldName}` placeholders are resolved from
 *    `row.original[fieldName]`.
 * - Throws immediately if any placeholder cannot be resolved or resolves to null/undefined.
 */
function resolveUrlPlaceholders(
  linkUrl: string,
  value: unknown,
  rowOriginal: Record<string, unknown>,
  linkUrlHasPlaceHolder?: boolean
): string {
  // Always resolve {value} first.
  const urlAfterValue = linkUrl.replace(/\{value\}/g, () => {
    if (value === null || value === undefined) {
      throw new Error(
        `Missing or null parameter "value" required for {value} placeholder replacement.`
      );
    }
    return String(value);
  });

  // Early exit when no additional row-based placeholders are expected.
  if (!linkUrlHasPlaceHolder) {
    return urlAfterValue;
  }

  // Resolve remaining row-field placeholders.
  return urlAfterValue.replace(/\{(\w+)\}/g, (_match, placeholderName: string) => {
    const fieldValue = rowOriginal[placeholderName];
    if (fieldValue === null || fieldValue === undefined) {
      throw new Error(
        `Missing or null field "${placeholderName}" required for linkUrl placeholder replacement.`
      );
    }
    return String(fieldValue);
  });
}

/**
 * Builds a `Cell` renderer for a link column.
 *
 * The `linkUrl` string may contain placeholders wrapped in curly braces
 * (e.g. `{userId}`, `{orderId}`, `{value}`). These are resolved at render
 * time using the row's original data object and the cell's current value.
 *
 * @param linkUrl        - URL template (e.g. `"/crm/admin/client/{code}/all/{id}"`).
 * @param linkIcon       - Icon name passed to `<IconSymbol>` (e.g. `"edit"`).
 * @param linkOpenWindow - When `true`, the link opens in a new browser window/tab.
 * @param linkShowValue  - When `true`, the raw cell value is rendered beside the icon.
 */
export function buildLinkCell(
  linkUrl: string,
  linkIcon: IconName,
  linkOpenWindow: boolean,
  linkShowValue: boolean,
  linkUrlHasPlaceHolder?: boolean
) {
  return ({ value, row }: { value: unknown; row: any }) => {
    if (value === null || value === undefined || value === "") {
      return linkShowValue ? "" : null;
    }

    const resolvedUrl = resolveUrlPlaceholders(linkUrl, value, row.original, linkUrlHasPlaceHolder);

    return (
      <Link
        fontWeight="soft"
        size="sm"
        href={resolvedUrl}
        openNewWindow={linkOpenWindow}
      >
        {linkIcon ? <IconSymbol icon={linkIcon} size="24" /> : null}
        {linkShowValue ? String(value) : null}
      </Link>
    );
  };
}

/**
 * React hook that returns a **new** array of `GridDataTableColumn`
 * definitions where every column with `linkColumn: true` has a `Cell`
 * renderer that wraps the cell value in a `<Link>` (and optionally an
 * `<IconSymbol>`).
 *
 * The original column objects are **not** mutated — the hook produces
 * shallow copies with the `Cell` property set, which avoids stale-
 * mutation bugs across HMR reloads and React strict-mode double renders.
 *
 * The result is memoized via `useMemo` and only recomputes when the
 * `columns` reference changes.
 *
 * The column must also specify:
 * - `linkUrl`  — URL template. Supports `{value}` for the cell value
 *   and `{fieldName}` for any field on `row.original`
 *   (e.g. `"/crm/admin/client/{code}/all/{id}"`).
 * - `linkIcon` — (optional) Icon name passed to the `<IconSymbol>`
 *   component (e.g. `"edit"`, `"bookmarks"`).
 *
 * Placeholder resolution rules:
 * - `{value}` → replaced with the cell's current `value` parameter.
 * - `{anyOtherName}` → replaced with `row.original[anyOtherName]`.
 * - Throws if any placeholder resolves to `null` or `undefined`.
 *
 * @example
 * ```tsx
 * const columns: GridDataTableColumn[] = [
 *   {
 *     Header: '',
 *     accessor: 'id',
 *     type: 'number',
 *     linkColumn: true,
 *     linkUrl: '/crm/admin/client/{code}/all/{id}',
 *     linkIcon: 'edit',
 *     linkShowValue: false,
 *   },
 * ];
 *
 * const mappedColumns = useDataTableLinkColumnMapper({ columns });
 *
 * const tableState = useDataTable({
 *   initialColumns: mappedColumns,
 *   // ...
 * });
 * ```
 */
export function useDataTableLinkColumnMapper({
  columns,
}: UseDataTableLinkColumnMapperOptions): GridDataTableColumn[] {
  return useMemo(() => {
    return columns.map((column) => {
      if (!column.linkColumn || !column.linkUrl) {
        return column;
      }

      return {
        ...column,
        Cell: buildLinkCell(
          column.linkUrl,
          column.linkIcon as IconName,
          column.linkOpenWindow ?? false,
          column.linkShowValue ?? true,
          column.linkUrlHasPlaceHolder,
        ),
      };
    });
  }, [columns]);
}
